import { signal } from "@preact/signals";
import type { Profile } from "@roguelik/shared-net";
import type {
  CameraStateMessage,
  MetricsMessage,
  NetInfo,
  OfferChoice,
  PlayerBuild,
  RosterMember,
} from "../protocol.js";

/**
 * Single source of UI truth. The main thread routes worker messages into these
 * signals; components subscribe. The 3D worker is untouched by any of this.
 */

export const net = signal<NetInfo | null>(null);
export const metrics = signal<MetricsMessage | null>(null);
export const camera = signal<CameraStateMessage | null>(null);
export const offer = signal<{
  level: number;
  choices: readonly OfferChoice[];
  /** Wall-clock ms when the server auto-picks (for the countdown bar); 0 = none. */
  deadline: number;
} | null>(null);
export const profile = signal<Profile | null>(null);
export const authStatus = signal<string>("");
export const build = signal<PlayerBuild | null>(null);
/** Lobby roster: who is currently in the room (slot + display name). */
export const roster = signal<readonly RosterMember[]>([]);
export const devOpen = signal<boolean>(false);
/** Locally-selected character id (lobby). Sent to the server on Start. */
export const selectedCharacter = signal<string>("");
/** The room's current map id (from the server welcome; lobby-selectable). */
export const roomMapId = signal<string>("arena");
/** Manual-aim engaged (B4): powers fire toward the mouse instead of auto-targeting. */
export const aimManual = signal<boolean>(false);
/** Dev/test escape hatch (B-dev): all characters + artifacts unlocked (5 clicks on the title). */
export const devUnlockAll = signal<boolean>(false);
/** ESC pause menu open (in-run): Resume / Restart / Return to lobby. */
export const pauseMenu = signal<boolean>(false);

/** Actions the UI invokes; wired once from `main.ts` at boot. */
export interface UiActions {
  choose(choiceId: number): void;
  action(kind: "start" | "restart" | "lobby" | "continue" | "ascend"): void;
  auth(req: { kind: "register" | "login"; username: string; password: string }): void;
  logout(): void;
  buy(artifactId: string): void;
  equip(artifactId: string, equipped: boolean): void;
  selectCharacter(id: string): void;
  /** Select the room's map (lobby). */
  selectMap(id: string): void;
  cameraToggle(): void;
  cycleLatency(): void;
  togglePrediction(): void;
  toggleRewind(): void;
  /** Toggle auto ↔ manual aim (B4). */
  toggleAim(): void;
  /** Dev/test escape hatch: unlock every character + artifact (B-dev). */
  devUnlock(): void;
  cameraTune(msg: {
    pitchDeltaDeg?: number;
    distanceDelta?: number;
    fovDelta?: number;
    logState?: boolean;
  }): void;
}

export const actions: { current: UiActions | null } = { current: null };
export const act = (): UiActions | null => actions.current;

/** True when any modal menu/overlay owns the screen (gates game/dev hotkeys). */
export function modalOpen(): boolean {
  if (offer.value !== null) return true;
  if (pauseMenu.value) return true;
  const phase = net.value?.runPhase;
  return phase === "lobby" || phase === "victory" || phase === "defeat";
}
